import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';

import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	abortRemixCreateExperience,
	abortRemixCreateExperienceIfNotCommitted,
	startRemixCreateExperience,
	succeedRemixCreateExperienceAtHandoff,
	trackRemixCreateOptionSelected,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';
import type { RemixContentComponentProps } from '@atlassian/malleable-ui-components/types';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';

import type { DynamicUiSource } from './common-types.ts';
import { createRemixContentComponentPlatform } from './EditorRemixCreatorPlatform.tsx';
import { prepareRemixFragmentNodes } from './filterRemixFragmentNodes.ts';
import { useRemixPreviewFlow } from './useRemixPreviewFlow.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';
import { EditorPageRemixModal as ObjectRemixComponent } from './PageRemixContainer.tsx';

type EditorRemixAifcHandoffExperienceAttributes = {
	featureArea: string;
	entryPoint: 'editor';
	remixType: RemixOptionSelectionParameters['type'];
	remixSubtype?: string;
	conversationChannelId?: string;
};

const getEditorRemixAifcHandoffExperienceAttributes = ({
	type,
	subType,
}: {
	type: RemixOptionSelectionParameters['type'];
	subType: string | undefined;
}): EditorRemixAifcHandoffExperienceAttributes | undefined => {
	if (type !== 'custom') {
		return undefined;
	}

	return {
		featureArea:
			getRemixFeatureArea({
				remixType: type,
				existingFeatureArea: 'remix',
			}) ?? 'remix',
		entryPoint: 'editor',
		remixType: type,
		...(subType && { remixSubtype: subType }),
	};
};

/**
 * Creates a content component for RemixContentComponent using RemixContainer.
 *
 * When the `confluence_malleable_ui_platform_remix` feature gate is enabled,
 * delegates to the platform-level RemixContentComponent from
 * @atlassian/malleable-ui-components instead.
 */
export const createRemixContentComponent = (): ((
	props: RemixContentComponentProps,
) => React.JSX.Element | null) => {
	if (fg('confluence_malleable_ui_platform_remix')) {
		return createRemixContentComponentPlatform();
	}

	const BlockRemixComponent = ({
		handleModalClose,
		selectedContext,
		editorApi,
		onStartThinking,
		onStopThinking,
		isUpdate,
		remixModalMetadata,
		remixSource,
	}: Extract<RemixContentComponentProps, { remixType?: 'block' }>) => {
		const isRemixCancelled = useRef(false);
		const hasCommittedRemixCreateTerminal = useRef(false);
		const isUpdateRef = useRef(isUpdate);
		isUpdateRef.current = isUpdate;
		const experienceTracker = useAIMateExperienceTracker();
		const openAIFCStagingModalRef = useRef<() => void>(() => {});
		const selectedSubTypeRef = useRef<string | undefined>(undefined);
		const conversationIdRef = useRef<string | undefined>(undefined);
		const shouldOpenRovoChatOnCompleteRef = useRef(false);
		const remixAifcHandoffExperienceAttributesRef = useRef<
			EditorRemixAifcHandoffExperienceAttributes | undefined
		>(undefined);

		const { dynamicUiType } = useStagingAreaState();
		const { showSmartCreationModal } = useSmartCreationModalState();
		const { openConversationAssistant } = useToggleConversationAssistant();

		const {
			showProgressBar,
			isComplete,
			onCompleteAnimationFinished,
			onActionReady,
			resetCompletion,
		} = useRemixPreviewFlow({
			entryPoint: 'editor',
			experienceTracker,
			dynamicUiType,
			selectedSubTypeRef,
			openAIFCStagingModalRef,
			isRemixCancelledRef: isRemixCancelled,
			conversationIdRef,
			shouldOpenPreview: () => {
				if (!shouldOpenRovoChatOnCompleteRef.current) {
					return true;
				}

				shouldOpenRovoChatOnCompleteRef.current = false;
				hasCommittedRemixCreateTerminal.current = true;
				experienceTracker.succeed({ name: ExperienceName.REMIX_START });
				void remixGeneratePreviewExperience.success();
				handleModalClose();
				if (conversationIdRef.current) {
					updatePageRovoParams({
						pathway: 'chat',
						conversationId: conversationIdRef.current,
					});
				}
				openConversationAssistant('sidebar');
				return false;
			},
		});

		const {
			sendAIFCPrompt,
			stopRemixing,
			isRemixError,
			isRemixAborted,
			remixStoreError,
			openAIFCStagingModal,
			closeAIFCStagingModal,
			resetRemixConversationId,
		} = useRemixToAIFC({ onActionReady });
		openAIFCStagingModalRef.current = openAIFCStagingModal;

		const abortRemixAifcHandoffExperience = useCallback(
			(reason: string) => {
				const attributes = remixAifcHandoffExperienceAttributesRef.current;

				if (!attributes) {
					return;
				}

				experienceTracker.abort({
					name: ExperienceName.REMIX_AIFC_HANDOFF,
					reason,
					attributes: {
						...attributes,
						reason,
					},
				});
				remixAifcHandoffExperienceAttributesRef.current = undefined;
			},
			[experienceTracker],
		);

		// Abort experience on unmount (overlay closed without selecting an option).
		// Uses a ref so the cleanup always calls the latest tracker reference.
		const experienceTrackerRef = useRef(experienceTracker);
		experienceTrackerRef.current = experienceTracker;
		useEffect(() => {
			return () => {
				experienceTrackerRef.current.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User closed remix overlay',
					attributes: { reason: 'User closed remix overlay' },
				});
				if (!isUpdateRef.current) {
					abortRemixCreateExperienceIfNotCommitted(
						experienceTrackerRef.current,
						hasCommittedRemixCreateTerminal.current,
					);
				}
				const attributes = remixAifcHandoffExperienceAttributesRef.current;

				if (attributes) {
					experienceTrackerRef.current.abort({
						name: ExperienceName.REMIX_AIFC_HANDOFF,
						reason: 'User closed remix overlay',
						attributes: {
							...attributes,
							reason: 'User closed remix overlay',
						},
					});
					remixAifcHandoffExperienceAttributesRef.current = undefined;
				}
			};
		}, []);

		const onRemixFlowActiveChange = useCallback(
			(active: boolean) => {
				if (active) {
					isRemixCancelled.current = false;
					shouldOpenRovoChatOnCompleteRef.current = false;
					// Clear stale completion state from any previous flow.
					resetCompletion();
					onStartThinking();
				} else {
					isRemixCancelled.current = true;
					shouldOpenRovoChatOnCompleteRef.current = false;
					// Abort the current experience before stopping
					experienceTracker.abort({
						name: ExperienceName.REMIX_START,
						reason: 'User cancelled remix thinking',
						attributes: { reason: 'User cancelled remix thinking' },
					});
					if (!isUpdate) {
						abortRemixCreateExperience(experienceTracker, 'User cancelled remix thinking');
					}
					abortRemixAifcHandoffExperience('User cancelled remix thinking');
					void remixGeneratePreviewExperience.abort({
						metadata: { reason: 'User cancelled remix thinking' },
					});
					stopRemixing();
					if (expValEquals('cc_maui_polish_changes_batch_2', 'isEnabled', true)) {
						onStopThinking({ reopenModal: false });
					} else {
						// Start a new experience for the reopened overlay
						const restartAttributes = {
							entryPoint: 'editor',
							remixSource: 'cancelledRemixThinking',
							originalRemixSource: remixSource,
							product: 'confluence',
						};
						experienceTracker.start({
							name: ExperienceName.REMIX_START,
							id: ExperienceName.REMIX_START,
							attributes: restartAttributes,
						});
						if (!isUpdate) {
							startRemixCreateExperience({ experienceTracker, attributes: restartAttributes });
						}
						onStopThinking({ reopenModal: true });
					}
				}
			},
			[
				onStartThinking,
				stopRemixing,
				onStopThinking,
				experienceTracker,
				isUpdate,
				remixSource,
				resetCompletion,
				abortRemixAifcHandoffExperience,
			],
		);

		// Fail experience and clean up thinking UI when Rovo encounters an error
		const prevIsRemixError = useRef(isRemixError);
		useEffect(() => {
			if (!prevIsRemixError.current && isRemixError && !isRemixCancelled.current) {
				const errorMessage = remixStoreError?.content ?? 'Rovo agent error during remix';
				experienceTracker.fail({
					name: ExperienceName.REMIX_START,
					error: new Error(errorMessage),
					attributes: {
						errorType: remixStoreError?.errorType,
						traceId: remixStoreError?.traceId,
						errorStatusCode: remixStoreError?.status_code,
					},
				});
				void remixGeneratePreviewExperience.failure({
					metadata: {
						errorType: remixStoreError?.errorType,
						traceId: remixStoreError?.traceId,
					},
				});
				onStopThinking({ reopenModal: true });
			}
			prevIsRemixError.current = isRemixError;
		}, [isRemixError, remixStoreError, onStopThinking, experienceTracker]);

		// Dismiss the thinking bar when the user stops the remix from the Rovo side chat.
		useEffect(() => {
			if (isRemixAborted && !isRemixCancelled.current) {
				const reason = 'User stopped from Rovo side chat';
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason,
				});
				if (!isUpdate) {
					abortRemixCreateExperience(experienceTracker, reason);
				}
				abortRemixAifcHandoffExperience(reason);
				onStopThinking(
					expValEquals('cc_maui_polish_changes_batch_2', 'isEnabled', true)
						? { reopenModal: false }
						: undefined,
				);
			}
		}, [
			isRemixAborted,
			isUpdate,
			onStopThinking,
			experienceTracker,
			abortRemixAifcHandoffExperience,
		]);

		// When an error occurs during remix, close the thinking bar and ephemeral
		// preview, then open the Rovo chat sidebar so the user can see the error.
		useEffect(() => {
			if (!isRemixCancelled.current && isRemixError) {
				// Close the ephemeral preview (staging modal) if it is open
				if (showSmartCreationModal) {
					closeAIFCStagingModal();
				}
				// Close the thinking bar and dismiss the remix UI entirely.
				// This mirrors the renderer's onClose() call which unmounts
				// the component and clears isModalOpen. Using handleModalClose
				// instead of onStopThinking because the first error effect
				// sets isModalOpen=true (via reopenModal), and onStopThinking
				// alone only clears isThinking — leaving the component mounted
				// and the progress bar visible.
				handleModalClose();
				// Open the Rovo chat sidebar to display the error information.
				// `openConversationAssistant` is idempotent (no-op if already
				// open), so it is safe to call on every render where
				// `isRemixError` is true — no infinite toggle loop.
				openConversationAssistant('sidebar');
				resetRemixConversationId();
			}
		}, [
			isRemixError,
			showSmartCreationModal,
			closeAIFCStagingModal,
			handleModalClose,
			openConversationAssistant,
			resetRemixConversationId,
		]);

		const constructDynamicUiSource = useCallback(async (): Promise<DynamicUiSource | undefined> => {
			const localIds = selectedContext?.localIds ?? [];
			const startLocalId = localIds[0] ?? '';
			const endLocalId = localIds[localIds.length - 1] ?? '';
			const fragmentAdf = prepareRemixFragmentNodes(selectedContext?.selectionFragment ?? null);

			let fullAdf: DocNode = { type: 'doc', version: 1, content: [] };
			if (editorApi) {
				fullAdf = await new Promise((resolve) => {
					try {
						editorApi.core?.actions?.requestDocument?.(
							(doc: any) => {
								resolve(doc as DocNode);
							},
							{ alwaysFire: true },
						);
					} catch {
						resolve({ type: 'doc', version: 1, content: [] });
					}
				});
			}

			// When the entire page is selected (e.g. cmd+a), selectionFragment is null
			// because ProseMirror's AllSelection does not produce a fragment in the
			// selection context. In that case fall back to the full document content
			// so that Remix still receives the source content it needs.
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const resolvedFragmentAdf = fragmentAdf ?? (fullAdf.content as any);

			return {
				contentType: 'page' as const,
				startLocalId,
				endLocalId,
				startIndex: selectedContext?.startIndex ?? 0,
				endIndex: selectedContext?.endIndex ?? -1,
				fragmentAdf: resolvedFragmentAdf,
				fullAdf,
			};
		}, [editorApi, selectedContext]);

		const onOptionSelect = useCallback(
			async ({ type, subType, userPrompt }: RemixOptionSelectionParameters) => {
				const isRovoChatSubmission =
					remixSource === 'rovoChat' && remixModalMetadata?.preselectedConversationId !== undefined;
				const isCustomOnlyPrompt = type === 'custom' && !subType && !!userPrompt?.trim();
				selectedSubTypeRef.current = subType;
				shouldOpenRovoChatOnCompleteRef.current =
					!isRovoChatSubmission &&
					isCustomOnlyPrompt &&
					expValEquals('cc_maui_polish_changes_batch_5', 'isEnabled', true);
				const remixAifcHandoffExperienceAttributes = getEditorRemixAifcHandoffExperienceAttributes({
					type,
					subType,
				});

				if (remixAifcHandoffExperienceAttributes) {
					remixAifcHandoffExperienceAttributesRef.current = remixAifcHandoffExperienceAttributes;
					experienceTracker.start({
						name: ExperienceName.REMIX_AIFC_HANDOFF,
						id: ExperienceName.REMIX_AIFC_HANDOFF,
						attributes: remixAifcHandoffExperienceAttributes,
					});
				}

				experienceTracker.updateAttributes({
					name: ExperienceName.REMIX_START,
					attributes: {
						featureArea:
							getRemixFeatureArea({
								remixType: type,
								existingFeatureArea: 'remix',
							}) ?? 'remix',
						remixType: type,
						remixSubtype: subType,
						includesFreegenPrompt: !!userPrompt,
					},
				});

				void remixGeneratePreviewExperience.start();
				remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
				remixGeneratePreviewExperience.addMetadata({
					product: 'confluence',
					interactionSource: remixModalMetadata?.interactionSource,
					remixType: type,
					remixSubtype: subType,
					entryPoint: 'editor',
					includesFreegenPrompt: !!userPrompt,
					...(type === 'infographic'
						? {
								isConfluenceInfographicsTwoStageEnabled: fg('confluence-infographics-two-stage'),
							}
						: {}),
				});

				let conversationChannelId: string | undefined;
				try {
					const dynamicUiSource = await constructDynamicUiSource();
					conversationChannelId = await sendAIFCPrompt({
						userPrompt: isRovoChatSubmission ? undefined : userPrompt,
						promptText: isRovoChatSubmission ? userPrompt : undefined,
						dynamicUiType: type,
						dynamicUiSubtype: subType,
						dynamicUiSource,
						interactionSource: remixModalMetadata?.interactionSource,
						conversationId: remixModalMetadata?.preselectedConversationId,
					});
				} catch (error) {
					if (!remixAifcHandoffExperienceAttributes) {
						throw error;
					}

					experienceTracker.fail({
						name: ExperienceName.REMIX_AIFC_HANDOFF,
						error:
							error instanceof Error ? error : new Error('Failed to complete Remix AIFC handoff'),
						attributes: remixAifcHandoffExperienceAttributes,
					});
					remixAifcHandoffExperienceAttributesRef.current = undefined;
					return;
				}
				// Store the conversationId so `useRemixPreviewFlow` can include it as
				// `conversationChannelId` on the REMIX_RENDERS_IN_MODAL start attributes
				// once `onActionReady` fires.
				conversationIdRef.current = conversationChannelId;
				if (remixAifcHandoffExperienceAttributes) {
					const attributes = {
						...remixAifcHandoffExperienceAttributes,
						conversationChannelId,
					};
					experienceTracker.succeed({
						name: ExperienceName.REMIX_AIFC_HANDOFF,
						attributes,
					});
					succeedRemixCreateExperienceAtHandoff(experienceTracker, {
						conversationId: conversationChannelId,
					});
					remixAifcHandoffExperienceAttributesRef.current = undefined;
				}
				experienceTracker.updateAttributes({
					name: ExperienceName.REMIX_START,
					attributes: { conversationChannelId },
				});
				if (!isUpdate) {
					hasCommittedRemixCreateTerminal.current = true;
					trackRemixCreateOptionSelected({
						experienceTracker,
						conversationId: conversationChannelId,
						remixType: type,
						remixSubtype: subType,
						includesFreegenPrompt: !!userPrompt,
					});
				}
			},
			[
				constructDynamicUiSource,
				sendAIFCPrompt,
				remixModalMetadata,
				experienceTracker,
				isUpdate,
				remixSource,
			],
		);

		return (
			<RemixContainer
				onClose={handleModalClose}
				onOptionSelect={onOptionSelect}
				onRemixFlowActiveChange={onRemixFlowActiveChange}
				autoSubmitSelection={
					remixModalMetadata?.preselectedType
						? {
								type: remixModalMetadata.preselectedType,
								subType: remixModalMetadata.preselectedSubType,
								userPrompt: remixModalMetadata.preselectedUserPrompt,
							}
						: undefined
				}
				isEphemeralViewVisible={showSmartCreationModal}
				showProgressBar={showProgressBar}
				isComplete={isComplete}
				onCompleteAnimationFinished={onCompleteAnimationFinished}
			/>
		);
	};

	const Wrapper = (props: RemixContentComponentProps) => {
		const { onStopThinking, isThinking, isModalOpen } = props;
		const { showSmartCreationModal } = useSmartCreationModalState();
		const isSmartCreationModalOpen = useRef(false);

		const isObjectRemix = props.remixType === 'object';

		useLayoutEffect(() => {
			// Selected content should remain highlighted throughout the entire flow even when ephemeral modal is open
			// We deselect the content when the ephemeral modal is closed
			if (
				isSmartCreationModalOpen.current &&
				!showSmartCreationModal &&
				isThinking &&
				(!isObjectRemix || !fg('maui_remixable_page_native_embeds'))
			) {
				onStopThinking();
			}
			isSmartCreationModalOpen.current = showSmartCreationModal;
		}, [showSmartCreationModal, isThinking, onStopThinking, isObjectRemix]);

		if (!(isModalOpen || isThinking)) {
			return null;
		}

		let remixContent;
		if (isObjectRemix) {
			const { remixType, adfDocument, ...restProps } = props;

			remixContent = fg('maui_remixable_page_native_embeds') ? (
				<ObjectRemixComponent {...restProps} remixType={remixType} adfDocument={adfDocument} />
			) : (
				<BlockRemixComponent {...restProps} remixType="block" />
			);
		} else {
			remixContent = <BlockRemixComponent {...props} />;
		}

		return <ConfluenceConversationAssistant>{remixContent}</ConfluenceConversationAssistant>;
	};

	return Wrapper;
};
